/**
 * DEV-010H UAT corrective pass · Site Survey API error normaliser.
 *
 * The backend routers for the Site Survey workflow return HTTPException
 * `detail` in several shapes:
 *   • string                              → auth/permission errors
 *   • { code, message, missing[], ... }   → survey_incomplete (422)
 *   • { code, message, touched[] }        → approved_spec_protected (409)
 *   • [{loc, msg, type, ...}]             → Pydantic validation (422)
 *
 * The Engineer / Operations UI previously did:
 *     alert(err?.response?.data?.detail || "…")
 * which stringifies an object to the literal `"[object Object]"`. This
 * helper centralises the coercion so every survey error path renders
 * an actual human-readable string.
 *
 * Returns an object:
 *   {
 *     message:   string,   // always a non-empty user-facing string
 *     code:      string?,  // machine-readable code if provided
 *     missing:   array?,   // structured missing-field list if provided
 *     touched:   array?,   // spec-touched fields if provided
 *     status:    number?,  // HTTP status
 *   }
 *
 * The `.toString()` result is guaranteed never to be "[object Object]".
 */
export function normaliseApiError(err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;

    // Message-first fallback chain — everything else is metadata.
    let message = "";
    let code    = undefined;
    let missing = undefined;
    let touched = undefined;

    if (typeof detail === "string" && detail.trim()) {
        message = detail;
    } else if (detail && typeof detail === "object") {
        if (Array.isArray(detail)) {
            // FastAPI/Pydantic validation array — pick the first `msg`.
            const first = detail.find((d) => d && typeof d === "object" && d.msg);
            message = first?.msg || "Please check the highlighted fields.";
        } else {
            code    = detail.code;
            missing = Array.isArray(detail.missing) ? detail.missing : undefined;
            touched = Array.isArray(detail.touched) ? detail.touched : undefined;
            if (typeof detail.message === "string" && detail.message.trim()) {
                message = detail.message;
            } else if (typeof detail.msg === "string" && detail.msg.trim()) {
                message = detail.msg;
            }
        }
    }

    if (!message) {
        message = err?.message || "Something went wrong. Please try again.";
    }

    const out = { message, code, missing, touched, status };
    // Absolutely guarantee String(normalised) never yields "[object Object]".
    Object.defineProperty(out, "toString", { value: () => out.message });
    return out;
}

/** Convenience: same as normaliseApiError(err).message. */
export function formatApiError(err) {
    return normaliseApiError(err).message;
}
